import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { Product } from '~shared/models';
import { RecommendedProductsType } from '~app/modules/catalog/models';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ProductsRecommenderService } from '~shared/services/products-recommender/products-recommender.service';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { uuidv4 } from '~shared/utils/uuid.util';
import { User } from 'configcat-js-ssr';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { BestSellingProductsService } from '~app/modules/custom-hub/services';
import {
  EXPERIMENT_VARIATIONS,
  FeatureToggleEnum,
  NUMBER_0,
  NUMBER_15,
  ZRP_RECOMMENDATIONS_FOR_YOU
} from '~shared/constants';
import { ApplicationSearchEventSource } from '~shared/modules/application-event/constants';
import { GaTrackingEventLabel, GATrackingEventType } from '~shared/modules/ga-tracking/constants';
import { OutOfScopeService } from '~shared/modules/catalog/services/out-of-scope.service';
import { OTHER_BUCKETS_TITLE, OtherBuckets } from '~shared/modules/catalog/constants/other-buckets.constants';
import { OutOfScopeCategory } from '~app/modules/generic/models';
import {
  ZRPElementsVisibilityInViewportApplicationEvent
} from '~shared/modules/application-event/models/ZRP-elements-visibility-in-viewport-application-event.model';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services';
import {
  ZRPPopularCategoryVisibilityInViewportApplicationEventModel
} from '~shared/modules/application-event/models/ZRP-popular-category-visibility-in-viewport-application-event.model';
import {
  ZRPOtherBucketsVisibilityInViewportApplicationEvent
} from '~shared/modules/application-event/models/ZRP-other-buckets-visibility-in-viewport-application-event';
import {
  CarouselItemsViewedApplicationEvent,
  SearchApplicationEvent,
  SelectItemApplicationEventModel,
  ViewItemListApplicationEventModel,
} from '~shared/modules/application-event/models';
import { CurrencyService } from '~shared/services/currency.service';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import {
  BLOOMREACH_CAMPAIGN_IDS
} from '~shared/modules/catalog/catalog-products-list/modules/catalog-body/zero-result-search/bloomreach-campaign-ids';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import { PlpVariantRestyleService } from '~shared/services/plp-variant-restyle/plp-variant-restyle.service';
import { OptimizelyService } from '~shared/services/optimizely.service';

enum ZeroResultPageType {
  isExpired = 'isExpired',
  hasFilters = 'hasFilters',
  isSearch = 'isSearch',
  isOther = 'isOther',
}

const zeroResultTitleText = 'CATALOG.CATALOG_PAGE.ZERO_RESULT.SEARCH.TITLE';
const zeroResultSubtitleText = 'CATALOG.CATALOG_PAGE.ZERO_RESULT.SEARCH.SUBTITLE';
const ZERO_RESULT_INFO = {
  isExpired: {
    title: 'CATALOG.CATALOG_PAGE.ZERO_RESULT.EXPIRED_PAGE.TITLE',
    subTitle: 'CATALOG.CATALOG_PAGE.ZERO_RESULT.EXPIRED_PAGE.SUBTITLE',
    imageUrl: null,
  },
  hasFilters: {
    title: 'CATALOG.CATALOG_PAGE.ZERO_RESULT.FILTER.TITLE',
    subTitle: 'CATALOG.CATALOG_PAGE.ZERO_RESULT.FILTER.SUBTITLE',
    imageUrl: null,
  },
  isOther: {
    title: zeroResultTitleText,
    subTitle: zeroResultSubtitleText,
    imageUrl: null,
  },
};

@Component({
  selector: 'app-search-zero-result-search',
  templateUrl: './zero-result-search.component.html',
  styleUrls: ['./zero-result-search.component.scss'],
})
export class ZeroResultSearchComponent implements OnInit {
  @Input()
  isMobile: boolean;
  @Input()
  isDesktop: boolean;
  @Input()
  searchTerm: string;
  @Input()
  recommendedProductsFromSearchResponse: Product[];
  @Input()
  recommendedProductsType: RecommendedProductsType;
  @Input()
  isCategoryPage: boolean;
  @Input()
  hasFilters: boolean = false;
  @Input()
  isExpired: boolean = false;

  private _zeroResultInfo: {
    title: string;
    subTitle: string;
    imageUrl: string;
  };

  private user: User = {
    identifier: uuidv4(),
    email: '',
    country: getCountryDomainCode(this.webAppBuyerRequest),
  };

  recommendedProducts$: Observable<Product[]>;
  popularProducts$: Observable<Product[]>;
  shouldShowAlternativeProductsCarousel;
  isQueryOutOfScope: boolean;
  eOtherBuckets = OtherBuckets;
  bucketTitles = OTHER_BUCKETS_TITLE;
  countryCategoriesCard: OutOfScopeCategory;
  isOutOfScopeCategoryGridEnabled$ = this.featureFlagService.isFeatureEnabledForCurrentCountry$(
    FeatureToggleEnum.ZERO_RESULT_OUT_OF_SCOPE_CATEGORIES
  );
  isNewPLPVariantRestyle$: Observable<boolean>;
  isPLP: boolean;
  recommendationServiceUsed: string;

  constructor(
    private recommenderService: ProductsRecommenderService,
    private featureFlagService: ActiveFeaturesConfigcatService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any,
    private applicationEventService: ApplicationEventService,
    private bestSellingProductsService: BestSellingProductsService,
    private outOfScopeService: OutOfScopeService,
    private priceTypeService: PriceTypeService,
    private currencyService: CurrencyService,
    private productsListUrlService: ProductsListUrlService,
    private plpVariantRestyleService: PlpVariantRestyleService,
    private optimizelyService: OptimizelyService) {
  }

  ngOnInit(): void {
    this.recommendedProducts$ = this.optimizelyService.getExperimentVariant(ZRP_RECOMMENDATIONS_FOR_YOU).pipe(
      switchMap((variant: EXPERIMENT_VARIATIONS) => {
        if (variant === EXPERIMENT_VARIATIONS.VARIANT_TWO) {
          this.recommendationServiceUsed = 'Google recommendations'
          return this.recommenderService.getGRecommendedProducts(this.searchTerm);
        }

        this.recommendationServiceUsed = 'BloomReach recommendations';
        const campaignId: string = BLOOMREACH_CAMPAIGN_IDS[MarketDetectionUtil?.getCountryCode()];
        return this.recommenderService.getBloomreachRecommendedProducts(campaignId);
      })
    );

    this.shouldShowAlternativeProductsCarousel =
      this.recommendedProductsType === RecommendedProductsType.ParsedTextProducts &&
      this.recommendedProductsFromSearchResponse &&
      this.recommendedProductsFromSearchResponse.length > NUMBER_0;

    if (!this.shouldShowAlternativeProductsCarousel) {
      this.popularProducts$ = this.bestSellingProductsService.getProducts([], NUMBER_15, NUMBER_0);
    }

    this.applicationEventService.publish(
      new SearchApplicationEvent(
        this.searchTerm,
        ApplicationSearchEventSource.ZeroResult,
        null,
        this.isExpired,
        this.hasFilters,
        null,
        0
      )
    );

    if (this.searchTerm) {
      this.isQueryOutOfScope = this.outOfScopeService.checkOutOfScopeListItems(this.searchTerm);

      if (this.isQueryOutOfScope) {
        this.populateCategoryData();
      }
    }

    this._zeroResultInfo = ZERO_RESULT_INFO[this.getZeroResultPageType()];
    this.isNewPLPVariantRestyle$ = this.plpVariantRestyleService.isNewPLPVariantRestyle();
    this.isPLP = this.plpVariantRestyleService.isPLP();
  }

  onProductClick(event: { product: Product; priceType: PriceTypes; index: number }, carousel: string): void {
    const searchTerm = this.productsListUrlService.getSearchTerm();
    this.applicationEventService.publish(
      new SelectItemApplicationEventModel(
        `${carousel}/${GATrackingEventType.SearchZeroResult}`,
        event.product,
        event.priceType,
        event.index,
        false,
        `Zero Results Page - Carousel: ${carousel} recommendations - Search term: ${this.searchTerm}`,
        '',
        searchTerm
      )
    );
  }

  onCarouselIsVisible(carousel: string, products: Product[]): void {
    this.applicationEventService.publish(
      new ViewItemListApplicationEventModel(
        `${carousel}/${GATrackingEventType.SearchZeroResult}`,
        products,
        this.priceTypeService.getPriceTypeSync(),
        this.currencyService.getCurrency(),
        products?.length,
        this.searchTerm,
        false
      )
    );
    this.applicationEventService.publish(
      new CarouselItemsViewedApplicationEvent(
        products,
        0,
        '',
        `${GaTrackingEventLabel.SearchZeroResultsPage} - Carousel: ${carousel} - Search term: ${this.searchTerm}`
      )
    );
  }

  onCategoriesCarouselIsVisible(carousel: string): void {
    this.applicationEventService.publish(
      new ZRPPopularCategoryVisibilityInViewportApplicationEventModel(GATrackingEventType.ZRPPopularCategories)
    );
  }

  onOtherBucketsBannerIsVisible(): void {
    this.applicationEventService.publish(
      new ZRPOtherBucketsVisibilityInViewportApplicationEvent(GATrackingEventType.ZRPOtherBuckets)
    );
  }

  onOutOfScopeBannerIsVisible(): void {
    this.applicationEventService.publish(
      new ZRPElementsVisibilityInViewportApplicationEvent(GATrackingEventType.ZRPOutOfScopeBannerVisibility)
    );
  }

  redirectToOtherBucket(bucket: OtherBuckets, searchTerm: string): void {
    this.outOfScopeService.redirectToOtherBucket(bucket, searchTerm);
  }

  populateCategoryData(): void {
    this.countryCategoriesCard = this.outOfScopeService.populateCountryCategoriesCards();
  }

  onOutOfScopeCategoryGridIsVisible(): void {
    this.applicationEventService.publish(
      new ZRPElementsVisibilityInViewportApplicationEvent(GATrackingEventType.ZRPOutOfScopeCategoryGrid)
    );
  }

  get zeroResultInfo(): { title: string; subTitle: string; imageUrl: string; } {
    return this._zeroResultInfo;
  }

  private getZeroResultPageType(): ZeroResultPageType {
    if (this.isExpired) {
      return ZeroResultPageType.isExpired;
    }

    if (this.hasFilters) {
      return ZeroResultPageType.hasFilters;
    }
    return ZeroResultPageType.isOther;
  }
}
